// screens/business/WelcomeBusinessScreen.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Dimensions,
  ScrollView,
  Modal,
  Alert,
  BackHandler,
  ActivityIndicator,
  Animated,
  RefreshControl,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';

import { useBusiness } from '../../../context/BusinessContext';
import { storage } from '../../../utils/storage';

const { width, height } = Dimensions.get('window');

// ==================== API CONFIGURATION ====================
const API_BASE = 'https://api.jobeng.ai/api';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ── DB logo resolution helper ────────────────────────────────────────────────
const resolveLogoUrl = (profile) => {
  if (!profile) return null;
  const raw = profile.logo || profile.logo_url || profile.company_logo
            || profile.banner_image_url || profile.avatar || null;
  if (!raw) return null;
  if (raw.startsWith('http')) return raw;
  return `https://api.jobeng.ai${raw.startsWith('/') ? '' : '/'}${raw}`;
};

const colors = {
  primary: '#0e4e96',
  primaryLight: '#4a7db5',
  secondary: '#7C3AED',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  white: '#ffffff',
};

export default function WelcomeBusinessScreen({ navigation, route }) {
  // Use Business Context
  const {
    user: contextUser,
    companyProfile: contextCompanyProfile,
    subscription: contextSubscription,
    saveCompanyProfile,
    teamMembers: contextTeamMembers,
    isOnline,
    apiAvailable,
    syncStatus,
    saveUser,
    
    saveSubscription,
    addNotification,
    ensureUserHasCompany,
    refreshData,
    isFeatureAvailable,
  } = useBusiness();

  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);
  const [featureModalVisible, setFeatureModalVisible] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [showBusinessSolutions, setShowBusinessSolutions] = useState(false);
  const [dbStats, setDbStats]         = useState({ activeJobs:0, teamCount:0, openTenders:0, pendingInvoices:0 });
  const [companyName, setCompanyName] = useState('');

  // Animation
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  useEffect(() => {
    // If navigated here after a successful payment — apply new subscription immediately
    const routeParams = route?.params || {};
    if (routeParams.refreshed && routeParams.subscription) {
      const newSub = routeParams.subscription;
      setSubscription(newSub);
      storage.setObject('business_subscription', newSub).catch(() => {});
      console.log(`[Welcome] Post-upgrade subscription applied: ${newSub.plan_name} £${newSub.price}/mo`);
    }
    initializeScreen();
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: 0, duration: 400, useNativeDriver: true }),
    ]).start();

    const backHandler = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
    return () => backHandler.remove();
  }, []);

  const initialLoadDone = useRef(false);
  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
        // First focus only — subsequent navigations use cached data
        // Pull-to-refresh still works for manual refresh
      }
    }, [companyId])
  );

  // React immediately when context company profile changes
  useEffect(() => {
    if (contextCompanyProfile?.id) {
      const name = contextCompanyProfile.company_name || contextCompanyProfile.name || '';
      setCompanyProfile(contextCompanyProfile);
      setCompanyLogo(resolveLogoUrl(contextCompanyProfile));
      setCompanyName(name);
      setCompanyId(String(contextCompanyProfile.id));
    }
  }, [contextCompanyProfile]);

  const refreshScreenData = async () => {
    try {
      setRefreshing(true);
      if (refreshData) await refreshData();

      if (contextUser) setUser(contextUser);

      // Always fetch fresh from DB — overrides any stale context
      const cid = contextCompanyProfile?.id
               || companyId
               || await storage.getItem('company_id')
               || await storage.getItem('company_id');

      if (cid) {
        setCompanyId(String(cid));
        await fetchCompanyFromDB(String(cid));
      }

      await loadSubscriptionFromAsyncStorage();
      if (contextTeamMembers) setTeamMembers(contextTeamMembers);
    } catch (error) {
      console.error('Error refreshing screen data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const loadCompanyLogo = async () => {
    try {
      // Priority 1: already loaded profile
      const logo1 = resolveLogoUrl(companyProfile);
      if (logo1) { setCompanyLogo(logo1); return; }
      // Priority 2: context
      const logo2 = resolveLogoUrl(contextCompanyProfile);
      if (logo2) { setCompanyLogo(logo2); return; }
      // Priority 3: AsyncStorage cache
      const cached = await storage.getObject('company_profile');
      if (cached) {
        const logo3 = resolveLogoUrl(cached);
        if (logo3) { setCompanyLogo(logo3); return; }
        // Priority 4: refresh from DB
        const cid = cached.id ? String(cached.id) : null;
        if (cid) fetchCompanyFromDB(cid);
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  const handleBackPress = () => {
    if (subscriptionModalVisible) {
      setSubscriptionModalVisible(false);
      return true;
    }
    if (featureModalVisible) {
      setFeatureModalVisible(false);
      return true;
    }
    if (showBusinessSolutions) {
      setShowBusinessSolutions(false);
      return true;
    }
    return false;
  };

  const initializeScreen = async () => {
    try {
      setIsLoading(true);

      if (contextUser) {
        setUser(contextUser);
        setUserId(String(contextUser.id));
      }
      
      if (contextCompanyProfile) {
        setCompanyProfile(contextCompanyProfile);
        setCompanyLogo(resolveLogoUrl(contextCompanyProfile));
        setCompanyName(contextCompanyProfile.company_name || contextCompanyProfile.name || '');
        const cid = String(contextCompanyProfile.id);
        setCompanyId(cid);
        // Context already synced — no need to fetch again
      }
      
      if (contextTeamMembers) {
        setTeamMembers(contextTeamMembers);
      }

      // Never return early based on context — always validate subscription from DB
      if (contextCompanyProfile && contextUser) {
        console.log('✅ Profile loaded from context — validating subscription from DB');
      }

      const storedUserId = await storage.getItem('user_id');
      
      console.log('📱 Initializing screen with userId:', storedUserId);
      
      if (storedUserId) {
        setUserId(storedUserId);
      }

      if (!contextUser) {
        await loadUserFromAsyncStorage();
      }
      
      if (!contextCompanyProfile) {
        await loadCompanyFromAsyncStorage();
      }

      // Always fetch subscription from DB to avoid stale cached data
      await loadSubscriptionFromAsyncStorage();
      
    } catch (error) {
      console.error('❌ Error initializing screen:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserFromAsyncStorage = async () => {
    try {
      const userData = await storage.getObject('user_data');
      if (userData) {
        setUser(userData);
        setUserId(String(userData.id));
      }
    } catch (error) {
      console.warn('Error loading user from AsyncStorage:', error);
    }
  };

  // ── Fetch company profile + logo from DB ─────────────────────────────────
  const fetchCompanyFromDB = async (cid) => {
    if (!cid) return;
    try {
      const tok = await storage.getItem('auth_token') || await storage.getItem('token') || '';
      const res = await fetch(
        `https://api.jobeng.ai/api/company-profile/${cid}`,
        { headers: { 'x-api-key': API_KEY, 'Authorization': `Bearer ${tok}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const profile = data.data || data.company || data;
      if (!profile || !profile.id) return;

      // Resolve logo — try all known field names, prepend base if relative
      const logo = resolveLogoUrl(profile);
      const normalised = {
        ...profile,
        logo,
        name: profile.company_name || profile.name || '',
      };
      setCompanyLogo(logo);
      setCompanyProfile(normalised);
      setCompanyName(normalised.name);
      await storage.setObject('company_profile', normalised);
      // Don't push to context here — context already has its own sync cycle

      // Load live stats from company dashboard endpoint
      try {
        const sr = await fetch(
          `https://api.jobeng.ai/api/company/${cid}/dashboard`,
          { headers: { 'x-api-key': API_KEY, 'Authorization': `Bearer ${tok}` } }
        );
        if (sr.ok) {
          const sd = await sr.json();
          const d  = sd.data || sd;
          setDbStats({
            activeJobs:      parseInt(d.active_jobs      || d.jobs      || 0),
            teamCount:       parseInt(d.team_count       || d.team      || 0),
            openTenders:     parseInt(d.open_tenders     || d.tenders   || 0),
            pendingInvoices: parseInt(d.pending_invoices || d.invoices  || 0),
          });
        }
      } catch {}

      // Load team
      try {
        const tr = await fetch(
          `https://api.jobeng.ai/api/company/${cid}/team`,
          { headers: { 'x-api-key': API_KEY, 'Authorization': `Bearer ${tok}` } }
        );
        if (tr.ok) {
          const td = await tr.json();
          const members = td.data || td.team || [];
          setTeamMembers(members);
          await storage.setObject('team_members', members);
        }
      } catch {}
    } catch (err) {
      console.warn('[WelcomeBusiness] DB fetch error:', err.message);
    }
  };

  const loadCompanyFromAsyncStorage = async () => {
    try {
      const companyData = await storage.getObject('company_profile');
      if (companyData) {
        setCompanyProfile(companyData);
        const logo = resolveLogoUrl(companyData);
        setCompanyLogo(logo);
        setCompanyName(companyData.company_name || companyData.name || '');
        if (companyData.id) {
          const cid = String(companyData.id);
          setCompanyId(cid);
          // Refresh from DB in background
          fetchCompanyFromDB(cid);
        }
      } else {
        // Try to load from DB using stored company_id
        const storedCid = await storage.getItem('company_id') || await storage.getItem('company_id');
        if (storedCid) {
          setCompanyId(storedCid);
          await fetchCompanyFromDB(storedCid);
        }
      }
      const teamData = await storage.getObject('team_members');
      if (teamData) setTeamMembers(teamData);
    } catch (error) {
      console.warn('Error loading company from AsyncStorage:', error);
    }
  };

  const loadSubscriptionFromAsyncStorage = async () => {
    try {
      // Skip DB fetch if we just navigated here from a payment — route param already set fresh data
      const routeParams = route?.params || {};
      if (routeParams.refreshed && routeParams.subscription) {
        console.log('[Sub] Skipping DB fetch — fresh data from payment navigation');
        return;
      }

      // One-time fix: clear any corrupt cache where plan_id/plan_name are mismatched
      const badCache = await storage.getObject('business_subscription').catch(() => null);
      if (badCache) {
        const pid   = badCache.plan_id || '';
        const pname = (badCache.plan_name || '').toLowerCase();
        const corrupt = (
          (pid.includes('enterprise') && !pname.includes('enterprise') && !pname.includes('business enterprise')) ||
          (pid.includes('multi_') && !pname.includes('team') && !pname.includes('business') && !pname.includes('enterprise') && !pname.includes('growth'))
        );
        if (corrupt) {
          console.log('[Cache] Clearing corrupt subscription cache:', pid, '≠', pname);
          await storage.removeItem('business_subscription');
        }
      }

      // Get company ID first — validate cache belongs to this company
      const cid = companyId
        || await storage.getItem('company_id')
        || await storage.getItem('company_id');

      const cached = await storage.getObject('business_subscription');
      const cacheValid = cached && cid && String(cached.company_id) === String(cid);
      // Only show cache if it matches current company (avoids showing wrong company's £599)
      if (cacheValid) setSubscription(cached);

      if (!cid) {
        if (!cached) createDefaultSubscription();
        return;
      }

      const tok = await storage.getItem('auth_token')
                || await storage.getItem('token') || '';

      const res = await fetch(
        `${API_BASE}/business-subscriptions?company_id=${cid}&status=active`,
        { headers: { 'x-api-key': API_KEY, 'Authorization': `Bearer ${tok}` } }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const sub  = data.data?.[0] || data.subscription || null;

      if (sub) {
        const planName = sub.plan_name || sub.name
          || sub.plan_id?.replace(/_/g,' ').replace(/\b\w/g, l=>l.toUpperCase())
          || 'Business Plan';
        const normalised = {
          id:            sub.id,
          plan_id:       sub.plan_id       || 'single_starter',
          plan_name:     planName,
          plan_type:     sub.plan_type     || 'single_user',
          // price first (DB already fixed), price_paid as fallback only
          price:         parseFloat(sub.price_paid || sub.price || 0),
          currency:      'GBP',
          billing_cycle: sub.billing_cycle || 'monthly',
          users:         parseInt(sub.max_users || sub.users || sub.users_count || 1),
          features:      Array.isArray(sub.features)      ? sub.features
                       : Array.isArray(sub.plan_features) ? sub.plan_features : [],
          status:        sub.status     || 'active',
          start_date:    sub.start_date || null,
          end_date:      sub.end_date   || null,
          auto_renew:    sub.auto_renew ?? true,
          company_id:   cid,
        };
        setSubscription(normalised);
        await storage.setObject('business_subscription', normalised);
        console.log(`[Sub] Company ${cid}: ${normalised.plan_name} £${normalised.price}/mo`);
      } else {
        if (!cacheValid) createDefaultSubscription();
      }
    } catch (error) {
      console.warn('[Subscription] DB fetch failed:', error.message);
      const cached = await storage.getObject('business_subscription').catch(() => null);
      if (!cached) createDefaultSubscription();
    }
  };


  const createDefaultSubscription = () => {
    const defaultSubscription = {
      id: null,
      plan_id: 'single_starter',
      plan_type: 'single_user',
      plan_name: 'Business Starter',
      price: 39,
      currency: 'GBP',
      billing_cycle: 'monthly',
      users: 1,
      features: ['Basic Features', 'Job Posting', 'Candidate Search'],
      status: 'active',
    };
    setSubscription(defaultSubscription);
    storage.setObject('business_subscription', defaultSubscription);
  };

  const allBusinessSolutions = [
    {
      category: 'Recruitment & Talent',
      icon: 'account-search',
      color: '#0e4e96',
      requiredPlan: 'starter',
      solutions: [
        { title:'Post Jobs',          description:'Publish engineering job listings',            icon:'briefcase-plus',      color:'#0e4e96', screen:'PostJobScreen',             requiredPlan:'starter',    benefits:['Reach 650k+ engineers','Instant publish','Application tracking'] },
        { title:'Candidate Search',   description:'Search our engineer database',               icon:'magnify',             color:'#0e4e96', screen:'CandidateSearch',           requiredPlan:'starter',    benefits:['Advanced filters','CV download','Direct messaging'] },
        { title:'Applicant Tracking', description:'Manage candidates through hiring pipeline',  icon:'account-multiple',    color:'#0e4e96', screen:'ApplicantTracking',         requiredPlan:'professional',benefits:['Pipeline management','Auto-scheduling','Offer letters'] },
        { title:'Interview Manager',  description:'Schedule and manage interviews',             icon:'calendar-check',      color:'#0e4e96', screen:'InterviewManager',          requiredPlan:'professional',benefits:['Calendar sync','Video links','Scorecards'] },
        { title:'Talent Pool',        description:'Build and manage your talent pipeline',      icon:'account-star',        color:'#0e4e96', screen:'TalentPool',                requiredPlan:'enterprise', benefits:['Passive candidates','Nurture campaigns','Analytics'] },
      ],
    },
    {
      category: 'HR & People Management',
      icon: 'account-group',
      color: '#10B981',
      requiredPlan: 'professional',
      solutions: [
        { title:'Employee Management',description:'Manage your workforce data',                 icon:'badge-account',       color:'#10B981', screen:'EmployeeManagementScreen',  requiredPlan:'professional',benefits:['Employee records','Role management','Document storage'] },
        { title:'Leave Management',   description:'Track and approve leave requests',           icon:'calendar-clock',      color:'#10B981', screen:'LeaveManagement',           requiredPlan:'professional',benefits:['Leave calendar','Auto-approvals','Balance tracking'] },
        { title:'Payroll',            description:'Process and manage payroll',                 icon:'currency-gbp',        color:'#10B981', screen:'PayrollManagementScreen',   requiredPlan:'enterprise', benefits:['BACS export','P60/P11D generation','HMRC compliant'] },
        { title:'Performance Reviews',description:'Run appraisals and track goals',             icon:'chart-bar',           color:'#10B981', screen:'PerformanceReviews',        requiredPlan:'enterprise', benefits:['360 reviews','Goal tracking','Competency frameworks'] },
        { title:'Training & CPD',     description:'Manage training and CPD records',            icon:'school',              color:'#10B981', screen:'TrainingManagement',         requiredPlan:'enterprise', benefits:['CPD tracking','Certificate storage','Course library'] },
      ],
    },
    {
      category: 'Finance & Payments',
      icon: 'cash-multiple',
      color: '#F59E0B',
      requiredPlan: 'starter',
      solutions: [
        { title:'Payment Centre',     description:'Invoices, payments and billing',             icon:'credit-card',         color:'#F59E0B', screen:'BusinessPaymentSystem',     requiredPlan:'starter',    benefits:['Invoice creation','Payment tracking','Reminders'] },
        { title:'Invoicing',          description:'Create and send professional invoices',      icon:'file-document',       color:'#F59E0B', screen:'InvoiceManager',            requiredPlan:'starter',    benefits:['Custom templates','PDF export','Auto-numbering'] },
        { title:'Expense Tracker',    description:'Track and approve business expenses',        icon:'receipt',             color:'#F59E0B', screen:'ExpenseTracker',            requiredPlan:'professional',benefits:['Receipt scanning','Category coding','HMRC ready'] },
        { title:'Financial Reports',  description:'P&L, cash flow and forecasting',             icon:'chart-line',          color:'#F59E0B', screen:'FinancialReports',          requiredPlan:'enterprise', benefits:['Real-time P&L','Budget vs actual','Board packs'] },
      ],
    },
    {
      category: 'Projects & Operations',
      icon: 'clipboard-list',
      color: '#8B5CF6',
      requiredPlan: 'professional',
      solutions: [
        { title:'Project Planning',   description:'Gantt charts, milestones and tasks',         icon:'chart-gantt',         color:'#8B5CF6', screen:'ProjectPlanningScreen',     requiredPlan:'professional',benefits:['Gantt charts','Resource planning','Risk register'] },
        { title:'Tenders & Bids',     description:'Manage UK tenders, bids and quotations',    icon:'file-search',         color:'#8B5CF6', screen:'TenderBidQuotation',        requiredPlan:'professional',benefits:['UK Gov tenders','Cost spreadsheets','PDF proposals'] },
        { title:'Document Control',   description:'Manage company documents and files',         icon:'folder-multiple',     color:'#8B5CF6', screen:'CompanyDocuments',          requiredPlan:'starter',    benefits:['Version control','Access levels','Audit trail'] },
        { title:'Team Management',    description:'Teams, departments and roles',               icon:'account-cog',         color:'#8B5CF6', screen:'TeamManagementScreen',      requiredPlan:'professional',benefits:['Org chart','Permissions','Team analytics'] },
        { title:'Analytics',          description:'Business intelligence and KPIs',             icon:'chart-box',           color:'#8B5CF6', screen:'TeamAnalyticsScreen',       requiredPlan:'enterprise', benefits:['Custom dashboards','KPI tracking','Export to Excel'] },
      ],
    },
    {
      category: 'Compliance & Safety',
      icon: 'shield-check',
      color: '#EF4444',
      requiredPlan: 'professional',
      solutions: [
        { title:'GDPR Compliance',    description:'Data protection and GDPR tools',             icon:'lock-check',          color:'#EF4444', screen:'GDPRCompliance',            requiredPlan:'professional',benefits:['DSAR handling','Data mapping','Breach reporting'] },
        { title:'Health & Safety',    description:'Risk assessments and H&S records',           icon:'hard-hat',            color:'#EF4444', screen:'HealthSafety',              requiredPlan:'professional',benefits:['RAMS builder','Incident log','Certification tracker'] },
        { title:'ISO Compliance',     description:'ISO 9001, 14001, 45001 management',         icon:'certificate',         color:'#EF4444', screen:'ISOCompliance',             requiredPlan:'enterprise', benefits:['Document control','Audit schedule','NCR management'] },
      ],
    },
    {
      category: 'Marketing & Growth',
      icon: 'bullhorn',
      color: '#EC4899',
      requiredPlan: 'professional',
      solutions: [
        { title:'Marketing',          description:'Campaigns, promotions and brand',            icon:'email-newsletter',    color:'#EC4899', screen:'MarketingPromotion',        requiredPlan:'professional',benefits:['Email campaigns','Social scheduling','Analytics'] },
        { title:'Blog & Content',     description:'Publish articles and thought leadership',    icon:'newspaper-variant',   color:'#EC4899', screen:'BlogEditorScreen',          requiredPlan:'professional',benefits:['SEO tools','Rich editor','Schedule publishing'] },
        { title:'Employer Brand',     description:'Showcase culture and values',                icon:'star-circle',         color:'#EC4899', screen:'EmployerBrand',             requiredPlan:'enterprise', benefits:['Company page','Photo gallery','Awards showcase'] },
      ],
    },
    {
      category: 'Business Profile',
      icon: 'office-building',
      color: '#6366F1',
      requiredPlan: 'starter',
      solutions: [
        { title:'Company Profile',    description:'Your public business profile',               icon:'domain',              color:'#6366F1', screen:'CompanyProfileScreen',      requiredPlan:'starter',    benefits:['Rich profile','Logo & branding','Location map'] },
        { title:'Logo & Assets',      description:'Upload and manage brand assets',             icon:'image',               color:'#6366F1', screen:'CompanyLogoUpload',         requiredPlan:'starter',    benefits:['Logo upload','Banner images','Brand colours'] },
        { title:'Locations',          description:'Manage office and site locations',           icon:'map-marker-multiple', color:'#6366F1', screen:'CompanyLocations',          requiredPlan:'professional',benefits:['Multiple sites','Maps integration','Contact details'] },
        { title:'Departments',        description:'Define company departments and structure',   icon:'sitemap',             color:'#6366F1', screen:'CompanyDepartments',        requiredPlan:'professional',benefits:['Org structure','Cost centres','Budget allocation'] },
      ],
    },
  ];

  const getQuickActions = () => [
    {
      id: 'dashboard',
      title: 'Business Dashboard',
      description: 'Access your main business dashboard',
      icon: 'view-dashboard',
      color: colors.primary,
      screen: 'BusinessDashboard',
      available: true,
    },
    {
      id: 'post_job',
      title: 'Post New Job',
      description: 'Create and publish job listings',
      icon: 'briefcase-plus',
      color: colors.success,
      screen: 'PostJobScreen',
      available: isFeatureAvailable('starter'),
    },
    {
      id: 'payment_center',
      title: 'Payment Center',
      description: 'Manage invoices, payments, and subscription',
      icon: 'credit-card',
      color: colors.primary,
      screen: 'BusinessPaymentSystem',
      available: true,
    },
    {
      id: 'subscription',
      title: 'Subscription Plans',
      description: 'View and upgrade your plan',
      icon: 'crown',
      color: colors.warning,
      screen: 'BusinessSubscriptionSystem',
      available: true,
    },
    {
      id: 'company_profile',
      title: 'Company Profile',
      description: 'Manage your company information',
      icon: 'office-building',
      color: colors.gray[600],
      screen: 'CompanyProfileScreen',
      available: true,
    },
  ];

  const handleFeatureSelect = (feature) => {
    if (isFeatureAvailable(feature.requiredPlan)) {
      navigation.navigate(feature.screen, {
        companyProfile: companyProfile,
        subscription: subscription,
        fromWelcome: true
      });
    } else {
      setSelectedFeature(feature);
      setFeatureModalVisible(true);
    }
  };

  const handleSubscriptionUpgrade = () => {
    navigation.navigate('BusinessSubscriptionSystem', {
      currentPlan: subscription,
      companyProfile: companyProfile
    });
  };

  const handlePlanSelected = (newSubscription) => {
    setSubscription(newSubscription);
    saveSubscription(newSubscription);
  };

  const getSubscriptionDisplay = () => {
    if (!subscription) {
      return {
        status: 'No Subscription',
        color: colors.danger,
        icon: 'alert-circle',
        message: 'Subscribe to unlock all features',
      };
    }
    const planId   = subscription.plan_id || '';
    const planName = subscription.plan_name || planId.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
    const price    = parseFloat(subscription.price_paid || subscription.price || 0);
    const isTrial  = subscription.status === 'trial' || planId === 'free_trial';

    // Expiry warning
    let daysLeft = null;
    if (subscription.end_date) {
      daysLeft = Math.ceil((new Date(subscription.end_date) - new Date()) / (1000*60*60*24));
    }
    const expiring = daysLeft !== null && daysLeft <= 7 && daysLeft >= 0;
    const expired  = daysLeft !== null && daysLeft < 0;

    if (expired) return {
      status: 'Subscription Expired', color: colors.danger,
      icon: 'alert-circle', message: `${planName} — renew now to restore access`,
    };
    if (expiring) return {
      status: `Expiring in ${daysLeft} day${daysLeft!==1?'s':''}`,
      color: colors.warning, icon: 'clock',
      message: `${planName} — tap to renew`,
    };
    if (isTrial) return {
      status: 'Free Trial', color: colors.warning, icon: 'star-outline',
      message: planName ? `${planName} — upgrade to unlock all features` : 'Upgrade to unlock all features',
    };
    return {
      status: `${planName}`, color: colors.success, icon: 'checkmark-circle',
      message: `£${price}/mo · ${subscription.max_users >= 999 ? 'Unlimited' : subscription.max_users || 1} user${(subscription.max_users||1)>1?'s':''} · Active`,
    };
  };

  const getInitials = (name) => {
    if (!name) return 'B';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'B';
  };

  const getFirstName = (fullName) => {
    if (!fullName) return 'Business Owner';
    return fullName.split(' ')[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading your business portal...</Text>
          {!isOnline && (
            <View style={styles.offlineModeContainer}>
              <Ionicons name="cloud-offline" size={20} color={colors.danger} />
              <Text style={styles.offlineModeText}>Offline Mode - Using cached data</Text>
            </View>
          )}
        </View>
      </SafeAreaView>
    );
  }

  const subscriptionStatus = getSubscriptionDisplay();
  const quickActions = getQuickActions();

  return (
    <SafeAreaView style={styles.safeArea}>
      <LinearGradient colors={[colors.primary, colors.primaryLight]} style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <MaterialCommunityIcons name="office-building" size={26} color="rgba(255,255,255,0.8)" />
          </View>
          <View style={styles.headerTextContainer}>
            <Text style={styles.headerTitle}>JOBENG Business</Text>
            <Text style={styles.headerSubtitle}>
              {companyName || companyProfile?.company_name || companyProfile?.name || user?.company_name || 'Business Portal'}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.profileButton}
            onPress={() => navigation.navigate('CompanyProfileScreen', { 
              companyProfile: companyProfile 
            })}
          >
            {companyProfile?.logo ? (
              <Image source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }} style={styles.profileImage} />
            ) : (
              <View style={styles.profilePlaceholder}>
                <Text style={styles.profileInitials}>
                  {getInitials(companyProfile?.name || user?.company_name || user?.full_name)}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
        {!isOnline && (
          <View style={styles.headerOfflineIndicator}>
            <Ionicons name="cloud-offline" size={14} color="#fff" />
            <Text style={styles.headerOfflineText}>Offline Mode</Text>
          </View>
        )}
        {syncStatus === 'syncing' && (
          <View style={styles.syncingIndicator}>
            <ActivityIndicator size="small" color="#fff" />
            <Text style={styles.syncingText}>Syncing...</Text>
          </View>
        )}
      </LinearGradient>

      <Animated.View style={[styles.scrollContainer, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}>
        <ScrollView 
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={refreshScreenData} />
          }
        >
          <View style={styles.welcomeSection}>
            <View style={styles.welcomeTextContainer}>
              <Text style={styles.welcomeTitle}>
                Welcome back, {getFirstName(user?.full_name || companyProfile?.name)}!
              </Text>
              <Text style={styles.welcomeDescription}>
                Manage your engineering business with our comprehensive platform
              </Text>
              {companyProfile && (
                <View style={styles.companyInfoContainer}>
                  <View style={styles.companyInfoBadge}>
                    <Ionicons name="business" size={14} color={colors.primary} />
                    <Text style={styles.companyInfoText}>{companyProfile.name}</Text>
                  </View>
                  {companyProfile.industry && (
                    <View style={styles.companyInfoBadge}>
                      <Ionicons name="briefcase" size={14} color={colors.primary} />
                      <Text style={styles.companyInfoText}>{companyProfile.industry}</Text>
                    </View>
                  )}
                </View>
              )}
            </View>
            <View style={styles.illustrationContainer}>
              {companyLogo ? (
                <Image source={{ uri: companyLogo || undefined }} style={styles.welcomeLogo} resizeMode="contain"/>
              ) : (
                <View style={styles.welcomeLogoPlaceholder}>
                  <MaterialCommunityIcons name="office-building" size={48} color={colors.primary}/>
                  <Text style={styles.welcomeLogoInitials}>
                    {(companyName||companyProfile?.name||'B').charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
            </View>
          </View>

          {companyId && (
            <View style={styles.companyIdCard}>
              <Ionicons name="finger-print" size={16} color={colors.primary} />
              <Text style={styles.companyIdText}>Company ID: {companyId}</Text>
              {isOnline && apiAvailable && (
                <View style={styles.syncedBadge}>
                  <Ionicons name="cloud-done" size={14} color={colors.white} />
                  <Text style={styles.syncedText}>Synced</Text>
                </View>
              )}
            </View>
          )}

          {/* ── DB Stats Bar ── */}
          {(dbStats.activeJobs > 0 || dbStats.teamCount > 0) && (
            <View style={styles.dbStatsBar}>
              <View style={styles.dbStatItem}>
                <MaterialCommunityIcons name="briefcase-check" size={18} color="#0e4e96"/>
                <Text style={styles.dbStatValue}>{dbStats.activeJobs}</Text>
                <Text style={styles.dbStatLabel}>Active Jobs</Text>
              </View>
              <View style={styles.dbStatDivider}/>
              <View style={styles.dbStatItem}>
                <MaterialCommunityIcons name="account-group" size={18} color="#10B981"/>
                <Text style={styles.dbStatValue}>{dbStats.teamCount}</Text>
                <Text style={styles.dbStatLabel}>Team</Text>
              </View>
              <View style={styles.dbStatDivider}/>
              <View style={styles.dbStatItem}>
                <MaterialCommunityIcons name="file-search" size={18} color="#8b5cf6"/>
                <Text style={styles.dbStatValue}>{dbStats.openTenders}</Text>
                <Text style={styles.dbStatLabel}>Tenders</Text>
              </View>
              <View style={styles.dbStatDivider}/>
              <View style={styles.dbStatItem}>
                <MaterialCommunityIcons name="receipt" size={18} color="#F59E0B"/>
                <Text style={styles.dbStatValue}>{dbStats.pendingInvoices}</Text>
                <Text style={styles.dbStatLabel}>Invoices</Text>
              </View>
            </View>
          )}

          <View style={styles.subscriptionCard}>
            <View style={styles.subscriptionHeader}>
              <View style={styles.subscriptionStatusContainer}>
                <Ionicons name={subscriptionStatus.icon} size={24} color={subscriptionStatus.color} />
                <View style={styles.subscriptionStatusText}>
                  <Text style={styles.subscriptionStatus}>{subscriptionStatus.status}</Text>
                  <Text style={styles.subscriptionMessage}>{subscriptionStatus.message}</Text>
                </View>
              </View>
              <TouchableOpacity style={styles.manageButton} onPress={() => setSubscriptionModalVisible(true)}>
                <Ionicons name="settings" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>
            {subscription && (
              <View style={styles.subscriptionDetails}>
                <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center' }}>
                  <Text style={styles.subscriptionPlan}>
                    {subscription.plan_name || subscription.plan_id?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}
                  </Text>
                  <Text style={[styles.subscriptionPrice, { fontSize:16, fontWeight:'800' }]}>
                    £{parseFloat(subscription.price_paid || subscription.price || 0).toFixed(2)}/mo
                  </Text>
                </View>
                <View style={{ flexDirection:'row', gap:12, marginTop:6, flexWrap:'wrap' }}>
                  {subscription.max_users > 0 && (
                    <Text style={styles.subscriptionUsers}>
                      👥 {subscription.max_users >= 999 ? 'Unlimited' : subscription.max_users} user{(subscription.max_users||1)>1?'s':''}
                    </Text>
                  )}
                  {subscription.billing_cycle && (
                    <Text style={styles.subscriptionUsers}>
                      🔄 {subscription.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}
                    </Text>
                  )}
                  {subscription.end_date && (
                    <Text style={styles.subscriptionUsers}>
                      📅 Renews {new Date(subscription.end_date).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}
                    </Text>
                  )}
                </View>
                {Array.isArray(subscription.features) && subscription.features.length > 0 && (
                  <View style={{ flexDirection:'row', flexWrap:'wrap', gap:6, marginTop:8 }}>
                    {subscription.features.slice(0,3).map((f,i) => (
                      <View key={i} style={{ backgroundColor:'rgba(14,78,150,0.08)', paddingHorizontal:8, paddingVertical:3, borderRadius:10 }}>
                        <Text style={{ fontSize:11, color:colors.primary, fontWeight:'600' }}>{f}</Text>
                      </View>
                    ))}
                    {subscription.features.length > 3 && (
                      <View style={{ backgroundColor:'rgba(14,78,150,0.08)', paddingHorizontal:8, paddingVertical:3, borderRadius:10 }}>
                        <Text style={{ fontSize:11, color:colors.primary, fontWeight:'600' }}>+{subscription.features.length-3} more</Text>
                      </View>
                    )}
                  </View>
                )}
              </View>
            )}
          </View>

          <View style={styles.quickActionsSection}>
            <Text style={styles.sectionTitle}>Quick Actions</Text>
            <View style={styles.quickActionsGrid}>
              {quickActions.map(action => (
                <TouchableOpacity
                  key={action.id}
                  style={[styles.quickActionCard, !action.available && styles.lockedCard]}
                  onPress={() => {
                    if (action.available) {
                      navigation.navigate(action.screen, {
                        companyProfile: companyProfile,
                        subscription: subscription,
                        fromWelcome: true
                      });
                    } else {
                      Alert.alert(
                        'Subscription Required',
                        'This feature requires a subscription. Would you like to upgrade?',
                        [
                          { text: 'Later', style: 'cancel' },
                          { text: 'Upgrade', onPress: handleSubscriptionUpgrade }
                        ]
                      );
                    }
                  }}
                >
                  <View style={[styles.quickActionIcon, { backgroundColor: `${action.color}20` }]}>
                    <MaterialCommunityIcons name={action.icon} size={24} color={action.available ? action.color : colors.gray[400]} />
                    {!action.available && (
                      <View style={styles.lockOverlay}>
                        <Ionicons name="lock-closed" size={12} color="#fff" />
                      </View>
                    )}
                  </View>
                  <Text style={[styles.quickActionTitle, !action.available && styles.lockedText]}>
                    {action.title}
                  </Text>
                  <Text style={[styles.quickActionDescription, !action.available && styles.lockedText]}>
                    {action.description}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.featuresSection}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Business Features</Text>
              <TouchableOpacity onPress={() => setShowBusinessSolutions(true)}>
                <Text style={styles.viewAllText}>View All</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.sectionDescription}>
              Comprehensive tools to grow and manage your engineering business
            </Text>
            {allBusinessSolutions.slice(0, 4).map(category => {
              const categoryAvailable = isFeatureAvailable(category.requiredPlan);
              return (
                <View key={category.category} style={styles.categoryContainer}>
                  <View style={styles.categoryHeader}>
                    <View style={[styles.categoryIcon, { backgroundColor: `${category.color}20` }]}>
                      <MaterialCommunityIcons name={category.icon} size={20} color={categoryAvailable ? category.color : colors.gray[400]} />
                    </View>
                    <Text style={[styles.categoryTitle, !categoryAvailable && styles.lockedText]}>
                      {category.category}
                    </Text>
                    {!categoryAvailable && (
                      <View style={styles.categoryRequiredBadge}>
                        <Text style={styles.categoryRequiredText}>{String(category.requiredPlan ?? '').toUpperCase()}</Text>
                      </View>
                    )}
                  </View>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categorySolutions}>
                    {category.solutions.slice(0, 5).map((solution, index) => {
                      const available = isFeatureAvailable(solution.requiredPlan);
                      return (
                        <TouchableOpacity
                          key={index}
                          style={[styles.solutionChip, !available && styles.lockedChip]}
                          onPress={() => handleFeatureSelect(solution)}
                        >
                          <MaterialCommunityIcons name={solution.icon} size={16} color={available ? category.color : colors.gray[400]} />
                          <Text style={[styles.solutionChipText, !available && styles.lockedText]}>
                            {solution.title}
                          </Text>
                          {!available && (
                            <View style={styles.solutionChipLock}>
                              <Ionicons name="lock-closed" size={10} color="#fff" />
                            </View>
                          )}
                        </TouchableOpacity>
                      );
                    })}
                    <TouchableOpacity 
                      style={styles.moreChip}
                      onPress={() => setShowBusinessSolutions(true)}
                    >
                      <Text style={styles.moreChipText}>More</Text>
                      <Ionicons name="chevron-forward" size={14} color={colors.primary} />
                    </TouchableOpacity>
                  </ScrollView>
                </View>
              );
            })}
          </View>

          <View style={styles.ctaSection}>
            {!subscription || parseFloat(subscription.price || 0) === 0 ? (
              <>
                <TouchableOpacity style={styles.primaryButton} onPress={handleSubscriptionUpgrade}>
                  <MaterialCommunityIcons name="crown" size={20} color="#fff" />
                  <Text style={styles.primaryButtonText}>Upgrade Your Plan</Text>
                </TouchableOpacity>
                <Text style={styles.ctaDescription}>
                  Unlock all features with a business subscription. Cancel anytime • No setup fees.
                </Text>
              </>
            ) : (
              <View style={{ gap: 10 }}>
                <TouchableOpacity
                  style={styles.primaryButton}
                  onPress={() => navigation.navigate('BusinessDashboard', {
                    companyProfile, subscription
                  })}
                >
                  <MaterialCommunityIcons name="view-dashboard" size={20} color="#fff" />
                  <Text style={styles.primaryButtonText}>Go to Dashboard</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.primaryButton, { backgroundColor:'rgba(14,78,150,0.12)' }]}
                  onPress={() => navigation.navigate('BusinessSubscriptionSystem', {
                    currentPlan: subscription, companyProfile
                  })}
                >
                  <MaterialCommunityIcons name="swap-horizontal" size={20} color={colors.primary} />
                  <Text style={[styles.primaryButtonText, { color: colors.primary }]}>Change Plan</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </ScrollView>
      </Animated.View>

      {/* Subscription Modal */}
      <Modal
        animationType="slide"
        transparent
        visible={subscriptionModalVisible}
        onRequestClose={() => setSubscriptionModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Subscription Management</Text>
              <TouchableOpacity onPress={() => setSubscriptionModalVisible(false)}>
                <Ionicons name="close" size={24} color={colors.gray[600]} />
              </TouchableOpacity>
            </View>
            <View style={styles.modalContent}>
              {subscription && (
                <>
                  <View style={styles.currentPlanCard}>
                    <Text style={styles.currentPlanTitle}>Current Plan</Text>
                    <Text style={styles.currentPlanName}>
                      {subscription.plan_name || subscription.plan_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Text>
                    <Text style={styles.currentPlanPrice}>
                      £{parseFloat(subscription.price_paid || subscription.price || 0).toFixed(2)}/{subscription.billing_cycle === 'annual' ? 'year' : 'month'}
                    </Text>
                    {subscription.end_date && (
                      <Text style={{ fontSize:12, color:colors.gray[500], marginTop:4 }}>
                        Renews: {new Date(subscription.end_date).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}
                      </Text>
                    )}
                    <View style={styles.planFeaturesList}>
                      {(subscription.features || []).slice(0, 5).map((feature, idx) => (
                        <View key={idx} style={styles.planFeatureItem}>
                          <Ionicons name="checkmark-circle" size={16} color={colors.success} />
                          <Text style={styles.planFeatureText}>{feature}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                  <View style={styles.modalActions}>
                    <TouchableOpacity style={styles.upgradeModalButton} onPress={() => {
                      setSubscriptionModalVisible(false);
                      handleSubscriptionUpgrade();
                    }}>
                      <Text style={styles.upgradeModalButtonText}>Upgrade Plan</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.manageModalButton}
                      onPress={() => {
                        setSubscriptionModalVisible(false);
                        navigation.navigate('BusinessPaymentSystem', { 
                          subscription: subscription,
                          companyProfile: companyProfile
                        });
                      }}
                    >
                      <Text style={styles.manageModalButtonText}>Manage Billing</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>
          </View>
        </View>
      </Modal>

      {/* Feature Upgrade Modal */}
      <Modal
        animationType="slide"
        transparent
        visible={featureModalVisible}
        onRequestClose={() => setFeatureModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Feature Locked</Text>
              <TouchableOpacity onPress={() => setFeatureModalVisible(false)}>
                <Ionicons name="close" size={24} color={colors.gray[600]} />
              </TouchableOpacity>
            </View>
            <View style={styles.modalContent}>
              {selectedFeature && (
                <>
                  <View style={styles.featureModalIcon}>
                    <MaterialCommunityIcons name={selectedFeature.icon} size={60} color={selectedFeature.color} />
                  </View>
                  <Text style={styles.featureModalTitle}>{selectedFeature.title}</Text>
                  <Text style={styles.featureModalDescription}>{selectedFeature.description}</Text>
                  <View style={styles.featureModalBenefits}>
                    <Text style={styles.benefitsTitle}>This feature includes:</Text>
                    {(selectedFeature.benefits || []).map((benefit, idx) => (
                      <View key={idx} style={styles.benefitModalItem}>
                        <Ionicons name="checkmark-circle" size={20} color={selectedFeature.color} />
                        <Text style={styles.benefitModalText}>{benefit}</Text>
                      </View>
                    ))}
                  </View>
                  <View style={styles.upgradePrompt}>
                    <Text style={styles.upgradePromptText}>
                      Upgrade to {selectedFeature.requiredPlan?.charAt(0).toUpperCase() + selectedFeature.requiredPlan?.slice(1)} plan to unlock this feature
                    </Text>
                  </View>
                  <TouchableOpacity style={styles.upgradeFeatureButton} onPress={() => {
                    setFeatureModalVisible(false);
                    handleSubscriptionUpgrade();
                  }}>
                    <Text style={styles.upgradeFeatureButtonText}>Upgrade Now</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        </View>
      </Modal>

      {/* Business Solutions Modal */}
      <Modal
        animationType="slide"
        transparent
        visible={showBusinessSolutions}
        onRequestClose={() => setShowBusinessSolutions(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, styles.fullModalContainer]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>All Business Solutions</Text>
              <TouchableOpacity onPress={() => setShowBusinessSolutions(false)}>
                <Ionicons name="close" size={24} color={colors.gray[600]} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalContent}>
              {allBusinessSolutions.map((category, catIndex) => {
                const categoryAvailable = isFeatureAvailable(category.requiredPlan);
                return (
                  <View key={catIndex} style={styles.modalCategoryContainer}>
                    <View style={styles.modalCategoryHeader}>
                      <View style={[styles.modalCategoryIcon, { backgroundColor: `${category.color}20` }]}>
                        <MaterialCommunityIcons name={category.icon} size={24} color={categoryAvailable ? category.color : colors.gray[400]} />
                      </View>
                      <View style={styles.modalCategoryTitleContainer}>
                        <Text style={[styles.modalCategoryTitle, !categoryAvailable && styles.lockedText]}>
                          {category.category}
                        </Text>
                        <Text style={styles.modalCategoryCount}>{category.solutions.length} solutions</Text>
                      </View>
                      {!categoryAvailable && (
                        <View style={styles.modalCategoryBadge}>
                          <Text style={styles.modalCategoryBadgeText}>{String(category.requiredPlan ?? '').toUpperCase()}</Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.modalSolutionsGrid}>
                      {category.solutions.map((solution, solIndex) => {
                        const available = isFeatureAvailable(solution.requiredPlan);
                        return (
                          <TouchableOpacity
                            key={solIndex}
                            style={[styles.modalSolutionCard, !available && styles.modalLockedCard]}
                            onPress={() => handleFeatureSelect(solution)}
                          >
                            <View style={[styles.modalSolutionIcon, { backgroundColor: `${category.color}20` }]}>
                              <MaterialCommunityIcons name={solution.icon} size={20} color={available ? category.color : colors.gray[400]} />
                              {!available && (
                                <View style={styles.modalSolutionLock}>
                                  <Ionicons name="lock-closed" size={10} color="#fff" />
                                </View>
                              )}
                            </View>
                            <Text style={[styles.modalSolutionTitle, !available && styles.lockedText]}>
                              {solution.title}
                            </Text>
                            <Text style={[styles.modalSolutionDescription, !available && styles.lockedText]}>
                              {solution.description}
                            </Text>
                            {!available && (
                              <View style={styles.modalSolutionBadge}>
                                <Text style={styles.modalSolutionBadgeText}>{solution.requiredPlan}</Text>
                              </View>
                            )}
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: colors.primary },
  header: { paddingTop: 20, paddingBottom: 24, paddingHorizontal: 20 },
  headerContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { width: 40, alignItems: 'flex-start' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTextContainer: { flex: 1, marginHorizontal: 12, alignItems: 'center' },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#fff' },
  headerSubtitle: { fontSize: 14, color: 'rgba(255,255,255,0.8)', marginTop: 2 },
  headerOfflineIndicator: { flexDirection: 'row', alignItems: 'center', marginTop: 8, backgroundColor: 'rgba(244, 67, 54, 0.2)', paddingHorizontal: 12, paddingVertical: 4, borderRadius: 16, alignSelf: 'flex-start' },
  headerOfflineText: { fontSize: 10, color: '#fff', marginLeft: 4 },
  syncingIndicator: { flexDirection: 'row', alignItems: 'center', marginTop: 8, backgroundColor: 'rgba(255,255,255,0.2)', paddingHorizontal: 12, paddingVertical: 4, borderRadius: 16, alignSelf: 'flex-start' },
  syncingText: { fontSize: 10, color: '#fff', marginLeft: 4 },
  profileButton: { width: 40, height: 40, borderRadius: 20, overflow: 'hidden' },
  profileImage: { width: 40, height: 40, borderRadius: 20 },
  profilePlaceholder: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  profileInitials: { fontSize: 14, fontWeight: 'bold', color: '#fff' },
  scrollContainer: { flex: 1, backgroundColor: colors.gray[50] },
  welcomeSection: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 24, backgroundColor: '#fff', marginBottom: 16 },
  welcomeTextContainer: { flex: 1, paddingRight: 16 },
  welcomeTitle: { fontSize: 24, fontWeight: 'bold', color: colors.gray[800], marginBottom: 8 },
  welcomeDescription: { fontSize: 16, color: colors.gray[600], lineHeight: 24, marginBottom: 16 },
  companyInfoContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  companyInfoBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.gray[100], paddingHorizontal: 10, paddingVertical: 4, borderRadius: 16 },
  companyInfoText: { fontSize: 12, color: colors.primary, marginLeft: 4, fontWeight: '500' },
  illustrationContainer: { justifyContent:'center', alignItems:'center' },
  welcomeLogo: { width:90, height:90, borderRadius:18, borderWidth:2, borderColor:colors.gray[200] },
  welcomeLogoPlaceholder: { width:90, height:90, borderRadius:18, backgroundColor:colors.gray[100], justifyContent:'center', alignItems:'center', borderWidth:2, borderColor:colors.gray[200] },
  welcomeLogoInitials: { position:'absolute', bottom:8, right:8, fontSize:22, fontWeight:'800', color:colors.primary },
  companyIdCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.gray[100], marginHorizontal: 20, marginBottom: 8, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 12, borderWidth: 1, borderColor: colors.success },
  companyIdText: { fontSize: 12, color: colors.gray[700], marginLeft: 8, flex: 1 },
  syncedBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.success, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  syncedText: { fontSize: 10, color: '#fff', marginLeft: 4 },
  subscriptionCard: { backgroundColor: '#fff', marginHorizontal: 20, marginBottom: 24, borderRadius: 16, padding: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 4 },
  subscriptionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  subscriptionStatusContainer: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  subscriptionStatusText: { marginLeft: 12, flex: 1 },
  subscriptionStatus: { fontSize: 16, fontWeight: 'bold', color: colors.gray[800] },
  subscriptionMessage: { fontSize: 14, color: colors.gray[600], marginTop: 2 },
  manageButton: { padding: 8 },
  subscriptionDetails: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16, borderTopWidth: 1, borderTopColor: colors.gray[200] },
  subscriptionPlan: { fontSize: 14, fontWeight: '600', color: colors.gray[800] },
  subscriptionPrice: { fontSize: 14, color: colors.gray[600] },
  subscriptionUsers: { fontSize: 12, color: colors.gray[600] },
  quickActionsSection: { paddingHorizontal: 20, marginBottom: 32 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: colors.gray[800], marginBottom: 8 },
  sectionDescription: { fontSize: 14, color: colors.gray[600], marginBottom: 20 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  viewAllText: { fontSize: 14, color: colors.primary, fontWeight: '600' },
  quickActionsGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  quickActionCard: { width: (width - 52) / 2, backgroundColor: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 4 },
  lockedCard: { opacity: 0.6 },
  quickActionIcon: { width: 48, height: 48, borderRadius: 24, justifyContent: 'center', alignItems: 'center', marginBottom: 12, position: 'relative' },
  lockOverlay: { position: 'absolute', top: -4, right: -4, width: 20, height: 20, borderRadius: 10, backgroundColor: colors.danger, justifyContent: 'center', alignItems: 'center' },
  quickActionTitle: { fontSize: 16, fontWeight: 'bold', color: colors.gray[800], marginBottom: 4 },
  lockedText: { color: colors.gray[400] },
  quickActionDescription: { fontSize: 12, color: colors.gray[600], lineHeight: 16 },
  featuresSection: { paddingHorizontal: 20, marginBottom: 32 },
  categoryContainer: { marginBottom: 24 },
  categoryHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  categoryIcon: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  categoryTitle: { fontSize: 16, fontWeight: '600', color: colors.gray[800], flex: 1 },
  categoryRequiredBadge: { backgroundColor: colors.gray[200], paddingHorizontal: 8, paddingVertical: 2, borderRadius: 12 },
  categoryRequiredText: { fontSize: 10, fontWeight: '600', color: colors.gray[700] },
  categorySolutions: { flexDirection: 'row' },
  solutionChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 6, marginRight: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1, position: 'relative' },
  lockedChip: { opacity: 0.6 },
  solutionChipText: { fontSize: 12, color: colors.gray[700], marginLeft: 4 },
  solutionChipLock: { position: 'absolute', top: -4, right: -4, width: 14, height: 14, borderRadius: 7, backgroundColor: colors.danger, justifyContent: 'center', alignItems: 'center' },
  moreChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.gray[100], borderRadius: 20, paddingHorizontal: 12, paddingVertical: 6, marginRight: 8 },
  moreChipText: { fontSize: 12, color: colors.primary, fontWeight: '600', marginRight: 2 },
  ctaSection: { paddingHorizontal: 20, paddingBottom: 32, alignItems: 'center' },
  primaryButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primary, paddingHorizontal: 32, paddingVertical: 16, borderRadius: 12, marginBottom: 16 },
  primaryButtonText: { color: '#fff', fontSize: 16, fontWeight: '600', marginLeft: 8 },
  ctaDescription: { fontSize: 14, color: colors.gray[600], textAlign: 'center', lineHeight: 20 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalContainer: { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: height * 0.8 },
  fullModalContainer: { height: height * 0.9 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: colors.gray[200] },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: colors.gray[800] },
  modalContent: { padding: 20 },
  currentPlanCard: { backgroundColor: colors.gray[100], borderRadius: 12, padding: 20, marginBottom: 24 },
  currentPlanTitle: { fontSize: 14, color: colors.gray[600], marginBottom: 8 },
  currentPlanName: { fontSize: 20, fontWeight: 'bold', color: colors.gray[800], marginBottom: 4 },
  currentPlanPrice: { fontSize: 16, color: colors.primary, marginBottom: 16 },
  planFeaturesList: { marginTop: 8 },
  planFeatureItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  planFeatureText: { fontSize: 14, color: colors.gray[800], marginLeft: 8 },
  modalActions: { flexDirection: 'row', gap: 12 },
  upgradeModalButton: { flex: 1, backgroundColor: colors.primary, paddingVertical: 16, borderRadius: 12, alignItems: 'center' },
  upgradeModalButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  manageModalButton: { flex: 1, backgroundColor: colors.gray[100], paddingVertical: 16, borderRadius: 12, alignItems: 'center', borderWidth: 1, borderColor: colors.gray[300] },
  manageModalButtonText: { color: colors.gray[600], fontSize: 16, fontWeight: '600' },
  featureModalIcon: { alignItems: 'center', marginBottom: 20 },
  featureModalTitle: { fontSize: 24, fontWeight: 'bold', color: colors.gray[800], textAlign: 'center', marginBottom: 12 },
  featureModalDescription: { fontSize: 16, color: colors.gray[600], textAlign: 'center', lineHeight: 24, marginBottom: 24 },
  featureModalBenefits: { marginBottom: 24 },
  benefitsTitle: { fontSize: 16, fontWeight: '600', color: colors.gray[800], marginBottom: 12 },
  benefitModalItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  benefitModalText: { fontSize: 14, color: colors.gray[800], marginLeft: 12 },
  upgradePrompt: { backgroundColor: colors.gray[200], borderRadius: 12, padding: 16, marginBottom: 24 },
  upgradePromptText: { fontSize: 14, color: colors.gray[700], textAlign: 'center', fontWeight: '500' },
  upgradeFeatureButton: { backgroundColor: colors.primary, paddingVertical: 16, borderRadius: 12, alignItems: 'center' },
  upgradeFeatureButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  dbStatsBar: { flexDirection:'row', backgroundColor:'#fff', marginHorizontal:20, marginBottom:12, borderRadius:14, padding:14, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.08, shadowRadius:6, elevation:3, alignItems:'center', justifyContent:'space-around' },
  dbStatItem: { alignItems:'center', flex:1, gap:3 },
  dbStatValue: { fontSize:18, fontWeight:'800', color:colors.gray[800] },
  dbStatLabel: { fontSize:10, color:colors.gray[500], fontWeight:'500', textAlign:'center' },
  dbStatDivider: { width:1, height:36, backgroundColor:colors.gray[200] },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.gray[50] },
  loadingText: { marginTop: 16, fontSize: 16, color: colors.gray[600], textAlign: 'center' },
  offlineModeContainer: { flexDirection: 'row', alignItems: 'center', marginTop: 16, paddingHorizontal: 20, paddingVertical: 8, backgroundColor: colors.gray[100], borderRadius: 20 },
  offlineModeText: { marginLeft: 8, fontSize: 12, color: colors.danger },
  modalCategoryContainer: { marginBottom: 24 },
  modalCategoryHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  modalCategoryIcon: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  modalCategoryTitleContainer: { flex: 1 },
  modalCategoryTitle: { fontSize: 16, fontWeight: '600', color: colors.gray[800], marginBottom: 2 },
  modalCategoryCount: { fontSize: 12, color: colors.gray[500] },
  modalCategoryBadge: { backgroundColor: colors.gray[200], paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12 },
  modalCategoryBadgeText: { fontSize: 10, fontWeight: '600', color: colors.gray[700] },
  modalSolutionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  modalSolutionCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, width: (width - 80) / 2, borderWidth: 1, borderColor: colors.gray[200], position: 'relative' },
  modalLockedCard: { opacity: 0.6 },
  modalSolutionIcon: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center', marginBottom: 8, position: 'relative' },
  modalSolutionLock: { position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: 8, backgroundColor: colors.danger, justifyContent: 'center', alignItems: 'center' },
  modalSolutionTitle: { fontSize: 14, fontWeight: '600', color: colors.gray[800], marginBottom: 4 },
  modalSolutionDescription: { fontSize: 11, color: colors.gray[600], lineHeight: 14, marginBottom: 8 },
  modalSolutionBadge: { backgroundColor: colors.gray[200], paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, alignSelf: 'flex-start' },
  modalSolutionBadgeText: { fontSize: 8, fontWeight: '600', color: colors.gray[700] },
});